import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, Signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCheckbox } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogClose, MatDialogRef } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { MonitoringActions } from '@states/monitoring/monitoring.action-types';
import { MonitoringSelectors } from '@states/monitoring/monitoring.selector-types';
import { UiButtonComponent, EnableEscCloseDirective, UiPhoneInputV2Component } from '@ui';
import { SharedModule } from 'src/app/shared/shared.module';
import { ContactSelectDialogData } from '../../../../contacts-select/contacts-select.component';
import { MonitoringModels } from 'src/app/pages/monitoring/models/monitoring.models';
@Component({
  selector: 'app-create-email-contact-dialog',
  imports: [
    TranslatePipe,
    MatDialogClose,
    ReactiveFormsModule,
    SharedModule,
    UiButtonComponent,
    MatCheckbox,
    MatTooltipModule,
    UiPhoneInputV2Component,
  ],
  templateUrl: './create-email-contact-dialog.component.html',
  styleUrl: './create-email-contact-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  hostDirectives: [EnableEscCloseDirective],
})
export class CreateEmailContactDialogComponent implements OnInit {
  private readonly store$ = inject(Store);
  private readonly destroyRef = inject(DestroyRef);
  private readonly actions$ = inject(Actions);
  private readonly dialogRef = inject(MatDialogRef<CreateEmailContactDialogComponent>);

  public readonly data: ContactSelectDialogData = inject(MAT_DIALOG_DATA);

  public selectIsCreatingLoader: Signal<boolean> = this.store$.selectSignal(
    MonitoringSelectors.selectIsCreating,
  );
  public selectIsLoadingLoader: Signal<boolean> = this.store$.selectSignal(
    MonitoringSelectors.selectIsLoading,
  );
  public selectAllOrganizationContacts: Signal<MonitoringModels.OrganizationContact[]> = this.store$.selectSignal(
    MonitoringSelectors.selectAllOrgContacts,
  );
  public selectAccountContacts: Signal<MonitoringModels.AccountContact[]> = this.store$.selectSignal(
    MonitoringSelectors.selectAccountContacts,
  );

  public contactForm: FormGroup = new FormGroup(
    {
      name: new FormControl('', [Validators.required]),
      phone: new FormControl('', []),
      email: new FormControl('', [Validators.required, Validators.email]),
      position: new FormControl(''),
      contactOnWeekend: new FormControl(false),
      contactOnShabbat: new FormControl(false),
    },
  );

  public ngOnInit(): void {
    if (this.data?.value) {
      this.contactForm.patchValue({
        email: this.data.value,
      });

      this.contactForm.controls['email']?.markAsTouched();
    }

    this.actions$
      .pipe(
        ofType(
          MonitoringActions.startCreateOrgContactSuccess,
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((data) => {
        this.dialogRef.close(data.contact);
      });
  }

  public createContact(): void {
    const formValues = this.contactForm.value;
    const phones = (formValues.phone ? [formValues.phone] : [])
      .filter((p: string) => p && p.trim())
      .map((p: string) => p.trim());

    const emails = ([formValues.email])
      .filter((e: string) => e && e.trim())
      .map((e: string) => e.trim());

    const requestValues = {
      name: formValues.name,
      phones,
      emails,
      position: formValues.position,
      contactOnWeekend: formValues.contactOnWeekend,
      contactOnShabbat: formValues.contactOnShabbat,
    };

    if (this.selectAllOrganizationContacts().length === 0 && this.selectAccountContacts().length === 0) {
      this.store$.dispatch(
        MonitoringActions.startCreateFirstOrgContact({
          contactInfo: requestValues,
        }),
      );
    } else {
      this.store$.dispatch(
        MonitoringActions.startCreateOrgContact({
          contactInfo: requestValues,
        }),
      );
    }
  }
}
